import React, { useEffect, useMemo } from "react";
import {
  useIsS,
  useIsXS,
  WSElement,
  WSGrid,
  WSModal
} from "@wingspanhq/fe-component-library";
import styles from "./index.module.scss";
import classNames from "classnames";
import { ModalStepper } from "./ModalStepper";
import { ModalNavigation } from "./ModalNavigation";
import {
  ModalFlowInternalState,
  ModalFlowProps,
  ModalFlowStepKey,
  StepComponentProps
} from "./types";
import { useBrowserPageTitle } from "../../hooks/useBrowserPageTitle";
import { useLocationStateStorage } from "../../hooks/useLocationStateStorage";

export const MODAL_UPLOAD_FLOW_STORAGE_KEY = "MODAL_UPLOAD_FLOW_STORAGE_KEY";

export function ModalFlow<Props extends {}>(props: ModalFlowProps<Props>) {
  useBrowserPageTitle(props.title);

  const isXS = useIsXS();
  const isS = useIsS();

  const [modalState, setModalState, resetModalState] = useLocationStateStorage<
    ModalFlowInternalState<Props>
  >(props.historyState, {
    flow: {
      stepKey: props.steps[0].key,
      subStepKey: props.steps[0]?.steps?.[0].key
    },
    ...props.initialState
  });

  const {
    flow: { stepKey, subStepKey, sideStepKey },
    state
  } = modalState;

  const steps = props.steps;
  const sideSteps = props.sideSteps;

  const sideStep = sideSteps?.find(step => step.key === sideStepKey);

  const stepIndex = steps.findIndex(step => step.key === stepKey) || 0;
  const currentStep = steps[stepIndex]!;
  const subStepIndex =
    currentStep?.steps?.findIndex(step => step.key === subStepKey) || 0;
  const currentSubStep = currentStep?.steps?.[subStepIndex];
  const activeTitle = currentStep?.title || props.title || "";

  const activeStep = sideStep || currentSubStep || currentStep;

  useEffect(
    () => () => {
      resetModalState();
    },
    []
  );

  const componentProps = useMemo(() => {
    const onClose = async () => {
      if (activeStep.confirmExit && props.confirmExitModal) {
        const confirm = await props.confirmExitModal.open();

        if (confirm) {
          props.onClose();
        }
      } else {
        props.onClose();
      }
    };

    const componentProps: StepComponentProps<Props> = {
      onClose() {
        onClose();
      },
      onSuccess() {
        props.onSuccess?.();
        onClose();
      },
      setState(newState: Partial<ModalFlowInternalState<Props>["state"]>) {
        setModalState(prevState => ({
          ...prevState,
          state: {
            ...prevState.state,
            ...newState
          }
        }));
      },
      state,
      onBack() {
        if (sideStep) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              sideStepKey: undefined
            }
          }));
          return;
        }

        if (subStepIndex > 0) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              subStepKey: currentStep.steps?.[subStepIndex - 1]?.key
            }
          }));
        } else if (stepIndex > 0) {
          const prevStep = steps[stepIndex - 1];

          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              stepKey: steps[subStepIndex - 1]?.key!,
              subStepKey: prevStep.steps?.[prevStep.steps!.length - 1]?.key
            }
          }));
        } else {
          onClose();
        }
      },
      onNext() {
        if (sideStep) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              sideStepKey: undefined
            }
          }));
          return;
        }

        if (
          currentStep.steps?.length &&
          subStepIndex < currentStep.steps.length - 1
        ) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              subStepKey: currentStep.steps[subStepIndex + 1]?.key
            }
          }));
        } else if (stepIndex < steps.length - 1) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              stepKey: steps[stepIndex + 1].key,
              subStepKey: steps[stepIndex + 1].steps?.[0]?.key
            }
          }));
        } else {
          props.onSuccess?.();
          onClose();
        }
      },
      goToStep(stepKey: ModalFlowStepKey, subStepKey?: ModalFlowStepKey) {
        setModalState(prevState => ({
          ...prevState,
          flow: {
            ...prevState.flow,
            stepKey,
            subStepKey
          }
        }));
      },
      goToSideStep(key: ModalFlowStepKey) {
        setModalState(prevState => ({
          ...prevState,
          flow: {
            ...prevState.flow,
            sideStepKey: key
          }
        }));
      }
    };

    return componentProps;
  }, [
    steps,
    currentStep,
    currentSubStep,
    stepIndex,
    subStepIndex,
    sideStep,
    state
  ]);

  const Component = sideStep
    ? sideStep.component
    : currentSubStep
    ? currentSubStep.component!
    : currentStep.component
    ? currentStep.component
    : () => null;

  return (
    <WSModal
      key="modal-flow"
      fullScreen
      fullScreenOnMobile
      blockClose={activeStep.confirmExit}
      titleAlign="center"
      colorBackground="gray100"
      title={activeTitle}
      onClose={props.onClose}
    >
      <WSElement className={classNames(styles.content, styles.twoColumn)}>
        <WSGrid>
          <WSGrid.Item span={{ m: "3" }}>
            {isXS || isS ? (
              <ModalStepper
                currentStep={currentStep}
                currentSubStep={currentSubStep}
                steps={steps}
                mb="L"
              />
            ) : (
              <ModalNavigation
                currentStep={currentStep}
                currentSubStep={currentSubStep}
                steps={steps}
              />
            )}
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "9" }}>
            <Component {...componentProps} />
          </WSGrid.Item>
        </WSGrid>
      </WSElement>
    </WSModal>
  );
}
